/**
@format
@flow
*/

import * as React from 'react';
import styled from 'styled-components';
import { Heading } from '@ziynx/components/typography';
import { Button } from '@ziynx/components/buttons';
import Link from '@ziynx/components/links';
import { Popover } from '@ziynx/components/popovers';
import type { TextType } from '@ziynx/flow-types/typography';
import type { PopoverType } from '@ziynx/flow-types/globals';
import { getDimensions } from '@ziynx/components/utilities';
import type { ButtonSquircleType } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';

type TextLinkType = {
  children?: any,
  extraSmall?: boolean,
  small?: boolean,
  medium?: boolean,
  large?: boolean,
  href?: string,
  color?: string,
  onClick?: (event?: SyntheticEvent<HTMLButtonElement | HTMLAnchorElement>) => any | Promise<any>,
  target?: string,
};

export type Props = $ReadOnly<{|
    /**
     * Title/Text for the headline
     */
    title?: TextType,
    /**
     * Background color
     */
    backgroundColor?: string,
    /**
     * Squircle Button
     */
    squircleButton?: ButtonSquircleType,
    /**
     * Popover prop, if no trigger is passed in, will use squircleButton
     */
    popover?: PopoverType,
    /**
     * Textlink for small, medium and large sizes
     */
    textLink?: TextLinkType | Array<TextLinkType>,
    /**
     * Set width for component
     */
    width?: string | number,
    /**
     * Children for text
     */
    children?: any,
|}>;

const Wrapper = styled.div`
    min-height: 46px;
    max-height: 92px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: ${({ width }) => getDimensions(width)};
    background: ${({ backgroundColor }) => backgroundColor};
    min-width: 288px;
`;

const StyledHeading = styled(Heading)`
    display: -webkit-box; 
    -webkit-line-clamp: 2; 
    -webkit-box-orient: vertical;   
    white-space: wrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const TextLinkWrapper = styled.div`
    display: flex;
    align-items: center;
    padding-left: 24px;
`;

const SquircleButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledTextLink = styled(Link)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    min-width: 32px;
    max-width: 194px;
    ${({ firstChild }) => firstChild && 'margin-right: 24px;'}
`;

const HeadlinePage = (props: Props): React$Node => {
  const {
    title,
    backgroundColor,
    squircleButton,
    popover,
    textLink,
    width,
    children,
    ...rest
  } = props;

  const renderSquircleButton = () => {
    const buttonSquircle = (
      <Button
        {...squircleButton}
        size="extraSmall"
        onClick={squircleButton?.onClick}
        icon={{
          name: squircleButton?.icon?.name || 'plus',
          size: squircleButton?.icon?.size || 'mini',
          color: squircleButton?.icon?.color || Colors.PrimaryBlue,
        }}
        variant="squircle"
      />
    );
    if (!squircleButton && !popover) return null;
    if (!popover) return buttonSquircle;
    return (
      <Popover
        links={popover?.links}
        position={popover?.position || 'bottomLeft'}
        trigger={popover?.trigger || buttonSquircle}
      >
        {popover?.children}
      </Popover>
    );
  };

  const renderTextLinks = () => {
    if (!textLink) return null;
    if (Array.isArray(textLink)) {
      return textLink.map((item, index) => (
        <StyledTextLink
          large
          {...item}
          key={index}
          firstChild={index === 0}
          variant="text"
        >
          {item?.children}
        </StyledTextLink>
      ));
    }
    return (
      <StyledTextLink large {...textLink} variant="text">
        {textLink?.children}
      </StyledTextLink>
    );
  };

  return (
    <Wrapper
      width={width || '100%'}
      backgroundColor={backgroundColor}
      {...rest}
    >
      <StyledHeading large color={title?.color || Colors.GrayVeryDark}>
        {title?.text}
        {children}
      </StyledHeading>
      <TextLinkWrapper>
        {!!squircleButton && !textLink
        && (
          <SquircleButtonWrapper>
            {renderSquircleButton()}
          </SquircleButtonWrapper>
        )}
        {!!textLink
          && renderTextLinks()}
      </TextLinkWrapper>
    </Wrapper>
  );
};

export default HeadlinePage;
